import { TextNode } from 'lexical';

// Extract the slug from a placeholder node's text field.
// Handles corrupted text like "{{slug}} " (trailing whitespace) or "{{slug}}, "
// (extra characters after the closing }}) by matching only what's inside {{ }}.
function extractSlug(text) {
  const m = (text || '').match(/\{\{([^}]+)\}\}/);
  return m ? m[1].trim() : null;
}

// Apply TextNode format flags (bold, italic, underline, strikethrough) as inline
// CSS on a DOM element. Called from exportDOM() so the styles survive the
// replacePlaceholders() step that only forwards the span's style="" attribute.
// Must be called AFTER element.style.cssText is set so individual setProperty
// calls add on top rather than being wiped by cssText assignment.
function applyFormatAsStyle(element, node) {
  if (node.hasFormat('bold'))               element.style.fontWeight     = 'bold';
  if (node.hasFormat('italic'))             element.style.fontStyle      = 'italic';
  const hasUnder  = node.hasFormat('underline');
  const hasStrike = node.hasFormat('strikethrough');
  if (hasUnder && hasStrike)               element.style.textDecoration = 'underline line-through';
  else if (hasUnder)                       element.style.textDecoration = 'underline';
  else if (hasStrike)                      element.style.textDecoration = 'line-through';
}

export class PlaceholderNode extends TextNode {
  static getType() {
    return 'placeholder';
  }

  static clone(node) {
    return new PlaceholderNode(node.__text, node.__key);
  }

  createDOM(config) {
    const dom = super.createDOM(config);
    dom.classList.add('lexical-placeholder-node');
    return dom;
  }

  updateDOM(prevNode, dom, config) {
    return super.updateDOM(prevNode, dom, config);
  }

  static importJSON(serializedNode) {
    // Normalise corrupted text: if the stored text has extra chars outside {{ }},
    // trim to just "{{slug}}" so the node stays clean on next save.
    const slug = extractSlug(serializedNode.text);
    const cleanText = slug ? `{{${slug}}}` : serializedNode.text;

    const node = $createPlaceholderNode(cleanText);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    // Do NOT restore mode from JSON — always keep 'token' so the node is atomic.
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON() {
    return { ...super.exportJSON(), type: 'placeholder', version: 1 };
  }

  // On HTML export (for preview/PDF), output a span with data-slug attribute.
  // __style is copied so font-size / color / etc. set by the admin are preserved
  // through to the doc-value-filled span that replacePlaceholders() creates.
  // __format flags (bold, italic, underline, strikethrough) are also applied as
  // inline styles so they survive the replacePlaceholders() style-forwarding step.
  exportDOM() {
    const slug = extractSlug(this.__text);

    // Phantom/corrupted node: text doesn't contain {{...}} — render as plain text
    // so it doesn't show up as a broken unfilled placeholder box.
    if (!slug) {
      const element = document.createElement('span');
      element.textContent = this.__text;
      if (this.__style) element.style.cssText = this.__style;
      applyFormatAsStyle(element, this);
      return { element };
    }

    const element = document.createElement('span');
    element.className = 'doc-placeholder-token';
    element.setAttribute('data-slug', slug);
    element.textContent = `{{${slug}}}`;
    if (this.__style) element.style.cssText = this.__style;
    applyFormatAsStyle(element, this);
    return { element };
  }

  static importDOM() {
    return {
      span: (node) => {
        if (!node.classList?.contains('doc-placeholder-token')) return null;
        return {
          conversion: (domNode) => ({
            node: $createPlaceholderNode(domNode.textContent || ''),
          }),
          priority: 1,
        };
      },
    };
  }

  // Do NOT override canInsertTextBefore/canInsertTextAfter — let the base
  // TextNode handle them based on mode. With 'token' mode the base class
  // already returns false for both, which prevents typing into the node.
  isSegmented() { return false; }
}

export function $createPlaceholderNode(text) {
  // Normal mode so the cursor can enter the node and the user can edit the
  // slug directly (e.g. rename {{name}} → {{firstName}}).  The
  // PlaceholderPlugin transform keeps the node valid: if the edited text no
  // longer matches {{...}} it is demoted back to a plain TextNode so the
  // pattern can be re-matched and a fresh PlaceholderNode created.
  return new PlaceholderNode(text);
}

export function $isPlaceholderNode(node) {
  return node instanceof PlaceholderNode;
}
